import CodingCertificate from '@components/common/coding-certificate'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import CourseDescription from '@components/course-landing-page/course-description'
import CourseModule from '@components/course-landing-page/course-module'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import SkillsYouWillGain from '@components/course-landing-page/skills-you-will-gain'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import StudentProjects from '@components/course-landing-page/student-projects'
import TestimonialsFold from '@components/course-landing-page/testimonial'
import CodingClassCatalog from '@components/home/coding-class-catalog'
import CodingalOffering from '@components/home/codingal-offering'
import Teachers from '@components/home/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import { COURSE_NAMES, COURSE_TITLES, FAQ } from '@lib/data/course-landing-page'
import { APComputerScienceA } from '@lib/seo'
import { Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'

const CourseCard = dynamic(() =>
  import('@components/course-landing-page/course-card')
)

const { AP_COMPUTER_SCIENCE_A } = COURSE_NAMES
// check wher course_code = 'ap-computer-science-a' in the COURSE_INFO, and from that take course query
const urlQuery = `?course=${COURSE_INFO['apcsa'].trialCourseQuery}`

const PythonForKids = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  useEffect(() => {
    const handleScroll = () => setShouldUpdateCourseCard(window.scrollY > 350)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useStoreUTMParams()

  return (
    <>
      <NextSeo {...APComputerScienceA} />
      <FAQPageJsonLd
        mainEntity={FAQ[AP_COMPUTER_SCIENCE_A].map(
          ({ acceptedAnswerText, questionName }) => ({
            acceptedAnswerText: acceptedAnswerText,
            questionName: questionName,
          })
        )}
      />
      <CourseJsonLd
        courseName="AP Computer Science A"
        description="Prepare for the AP Computer Science A exam with live 1:1 online classes. Learn fundamentals of java programming, data structures, algorithms and much more."
        provider={{
          '@type': 'Organization',
          name: 'Codingal',
        }}
        offers={{
          '@type': 'Offer',
          category: 'Paid',
        }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT48H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        shouldHideCourseCard={shouldUpdateCourseCard}
        courseName={AP_COMPUTER_SCIENCE_A}
        urlQuery={urlQuery}
      />
      <div className="container flex justify-between pt-10 mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <CourseDescription courseName={AP_COMPUTER_SCIENCE_A} />
          <CourseModule
            courseName={AP_COMPUTER_SCIENCE_A}
            heading="What you'll learn from our AP Computer Science A class curriculum"
          />
          <Teachers
            isCoursePage
            urlQuery={urlQuery}
            title={
              <Trans>
                Learn AP Computer Science A{' '}
                <span className="yellow-underline">experienced teachers</span>.
                Our hand-picked coding tutors ensure the best learning
                experience.
              </Trans>
            }
          />
          <SkillsYouWillGain
            courseName={AP_COMPUTER_SCIENCE_A}
            urlQuery={urlQuery}
          />
          <CodingalOffering
            offeringType="prime"
            isCoursePage
            urlQuery={urlQuery}
          />
          <CodingCertificate
            title={
              <>
                Unlock your skills and earn your{' '}
                <span className="yellow-underline">
                  AP Computer Science A certification
                </span>
              </>
            }
            description={
              "In this specialized AP Computer Science A course, learners in grades 11 and 12 earn STEM.org-certified coding certificates and prepare for competitive exams through rigorous exam prep and practice that not only celebrate their achievements but also equip them for one of the most prestigious programming exams. Students unlock special badges and leaderboard rankings, making programming a fun and rewarding experience. Students can earn the Java Developer, Basic DSA Programmer, and AP Computer Science A Champion certificates. These certifications are easy to download and share, are STEM.org accredited, and can be added to your child's learning portfolio for sharing with the world and on social media."
            }
            imageWidth={230}
            imageHeight={195}
          />
          <Activities courseName={AP_COMPUTER_SCIENCE_A} />
          <StudentProjects courseName={AP_COMPUTER_SCIENCE_A} />
          <StemAccredited
            courseName={COURSE_TITLES[AP_COMPUTER_SCIENCE_A]}
            urlQuery={urlQuery}
            heading="AP Computer Science A provides students certificates accredited by STEM.org"
          />
          <TestimonialsFold
            courseName={AP_COMPUTER_SCIENCE_A}
            urlQuery={urlQuery}
            heading="Codingal’s training program is loved by students and trusted by parents"
          />
          <Faq courseName={AP_COMPUTER_SCIENCE_A} />
          <RecommendedCourses courseCodes={['wd', 'aigc', 'pc']} />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={AP_COMPUTER_SCIENCE_A}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <CodingClassCatalog
        title={
          <>
            Our <span className="yellow-underline">AP Computer Science A</span>{' '}
            course is appropriate for High School students
          </>
        }
        description={
          <>
            <p>
              Our AP Computer Science A course is suitable for high school
              students in grades 11 and 12. This course covers the basics of the
              most popular programming language, Java. Students learn concepts
              like data types, loops, conditionals, and functions, and they
              learn to create patterns and apply OOP concepts, including
              classes, objects, libraries, polymorphism, encapsulation,
              inheritance, abstraction, and file handling. Students are also
              prepared for competitive exams as they go through previous AP
              Computer Science A exam prep questions. Additionally, students are
              introduced to an array of topics such as basic problem-solving in
              strings, searching and sorting algorithms, and the recursion
              process.
            </p>
            <p>
              Codingal supports kids ages 6 to 18 in grades K to 12 by providing
              engaging and comprehensive coding education that fosters
              creativity and critical thinking skills.
            </p>
          </>
        }
      />
      <FooterBanner
        title="AP Computer Science A course with best coding instructors"
        imagePath="/images/courses/ap-computer-science-a/footer-banner"
        mobileImagerPath="/images/courses/ap-computer-science-a/mobile-footer-banner"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return {
    props: {},
  }
}

export default PythonForKids
